import type {
  GenerationBudget,
  ValidationIssue,
  ValidationMetrics,
  ValidationReport,
} from '../contracts/generation.js';
import {
  ID_PATTERN,
  type GeometryRecipe,
  type SceneSpecV1,
} from '../contracts/scene-spec.js';
import { structuralHash } from '../domain/hash.js';
import {
  isFiniteNumber,
  isFiniteVector,
  magnitude,
} from '../domain/math.js';
import {
  MAX_PIXEL_TEXT_LENGTH,
  PIXEL_TEXT_PATTERN,
  pixelTextStrokes,
} from '../domain/pixel-font.js';

const COLOR_PATTERN = /^#[0-9a-f]{6}$/iu;
const SURFACE_PATTERNS = new Set([
  'fine-resin',
  'brushed-steel',
  'solar-weave',
]);
const LOCAL_CLOCK_TEXT_FORMATS = new Set([
  'HH:MM',
  'SS',
  'DD',
  'M.DD',
]);

const estimateTriangles = (geometry: GeometryRecipe): number => {
  switch (geometry.kind) {
    case 'box':
      return 12;
    case 'roundedPanel':
      return 12 * Math.pow(geometry.segments * 2 + 1, 2);
    case 'chamferedPanel':
      return 1_500;
    case 'cylinder':
      return geometry.radialSegments * 4;
    case 'sphere':
      return geometry.widthSegments * geometry.heightSegments * 2;
    case 'torus':
      return geometry.radialSegments * geometry.tubularSegments * 2;
    case 'capsule':
      return (
        geometry.radialSegments *
        (geometry.capSegments * 2 + 1) *
        2
      );
    case 'fanBlade':
      return 24;
    case 'pixelText':
      return pixelTextStrokes(geometry.text).length * 12;
    case 'vectorText':
      return geometry.text.length * 516;
  }
};

const validateGeometry = (
  geometry: GeometryRecipe,
  path: string,
  issues: ValidationIssue[],
): void => {
  const positive = (value: number, field: string): void => {
    if (!isFiniteNumber(value) || value <= 0) {
      issues.push({
        severity: 'error',
        code: 'geometry.non_positive',
        path: `${path}.${field}`,
        message: `${field} must be finite and greater than zero.`,
      });
    }
  };
  const segments = (value: number, field: string): void => {
    if (
      !Number.isInteger(value) ||
      value < 3 ||
      value > 512
    ) {
      issues.push({
        severity: 'error',
        code: 'geometry.invalid_segments',
        path: `${path}.${field}`,
        message: `${field} must be an integer within [3, 512].`,
      });
    }
  };

  switch (geometry.kind) {
    case 'box':
      geometry.size.forEach((value, index) =>
        positive(value, `size[${index}]`),
      );
      break;
    case 'roundedPanel': {
      geometry.size.forEach((value, index) =>
        positive(value, `size[${index}]`),
      );
      positive(geometry.radius, 'radius');
      if (
        !Number.isInteger(geometry.segments) ||
        geometry.segments < 1 ||
        geometry.segments > 12
      ) {
        issues.push({
          severity: 'error',
          code: 'geometry.invalid_segments',
          path: `${path}.segments`,
          message: 'roundedPanel.segments must be an integer within [1, 12].',
        });
      }
      if (
        isFiniteNumber(geometry.radius) &&
        geometry.radius >
          Math.min(geometry.size[0], geometry.size[1]) / 2
      ) {
        issues.push({
          severity: 'error',
          code: 'geometry.invalid_radius',
          path: `${path}.radius`,
          message:
            'roundedPanel.radius must not exceed half of its width or height.',
        });
      }
      break;
    }
    case 'chamferedPanel': {
      geometry.size.forEach((value, index) =>
        positive(value, `size[${index}]`),
      );
      positive(geometry.cornerCut, 'cornerCut');
      positive(geometry.bevelSize, 'bevelSize');
      if (
        !isFiniteNumber(geometry.waistInset) ||
        geometry.waistInset < 0
      ) {
        issues.push({
          severity: 'error',
          code: 'geometry.invalid_waist',
          path: `${path}.waistInset`,
          message: 'chamferedPanel.waistInset must be finite and non-negative.',
        });
      }
      if (
        isFiniteVector(geometry.size, 3) &&
        geometry.cornerCut >=
          Math.min(geometry.size[0], geometry.size[1]) / 3
      ) {
        issues.push({
          severity: 'error',
          code: 'geometry.invalid_corner_cut',
          path: `${path}.cornerCut`,
          message:
            'chamferedPanel.cornerCut must be less than one third of width/height.',
        });
      }
      if (
        isFiniteVector(geometry.size, 3) &&
        isFiniteNumber(geometry.waistInset) &&
        geometry.waistInset >= geometry.size[0] / 3
      ) {
        issues.push({
          severity: 'error',
          code: 'geometry.invalid_waist',
          path: `${path}.waistInset`,
          message:
            'chamferedPanel.waistInset must be less than one third of width.',
        });
      }
      if (
        isFiniteVector(geometry.size, 3) &&
        geometry.bevelSize >= geometry.size[2] / 2
      ) {
        issues.push({
          severity: 'error',
          code: 'geometry.invalid_bevel',
          path: `${path}.bevelSize`,
          message:
            'chamferedPanel.bevelSize must be less than half of depth.',
        });
      }
      break;
    }
    case 'cylinder':
      positive(geometry.radiusTop, 'radiusTop');
      positive(geometry.radiusBottom, 'radiusBottom');
      positive(geometry.height, 'height');
      segments(geometry.radialSegments, 'radialSegments');
      break;
    case 'sphere':
      positive(geometry.radius, 'radius');
      segments(geometry.widthSegments, 'widthSegments');
      segments(geometry.heightSegments, 'heightSegments');
      break;
    case 'torus':
      positive(geometry.radius, 'radius');
      positive(geometry.tube, 'tube');
      segments(geometry.radialSegments, 'radialSegments');
      segments(geometry.tubularSegments, 'tubularSegments');
      positive(geometry.arc, 'arc');
      break;
    case 'capsule':
      positive(geometry.radius, 'radius');
      positive(geometry.length, 'length');
      segments(geometry.capSegments, 'capSegments');
      segments(geometry.radialSegments, 'radialSegments');
      break;
    case 'fanBlade':
      positive(geometry.innerRadius, 'innerRadius');
      positive(geometry.outerRadius, 'outerRadius');
      positive(geometry.chord, 'chord');
      positive(geometry.thickness, 'thickness');
      if (geometry.outerRadius <= geometry.innerRadius) {
        issues.push({
          severity: 'error',
          code: 'geometry.invalid_blade_radius',
          path,
          message: 'fanBlade.outerRadius must be greater than innerRadius.',
        });
      }
      if (!isFiniteNumber(geometry.skew)) {
        issues.push({
          severity: 'error',
          code: 'geometry.invalid_skew',
          path: `${path}.skew`,
          message: 'fanBlade.skew must be finite.',
        });
      }
      break;
    case 'pixelText':
      positive(geometry.width, 'width');
      positive(geometry.height, 'height');
      positive(geometry.depth, 'depth');
      if (
        geometry.text.length === 0 ||
        geometry.text.length > MAX_PIXEL_TEXT_LENGTH
      ) {
        issues.push({
          severity: 'error',
          code: 'geometry.invalid_text_length',
          path: `${path}.text`,
          message: `pixelText.text must contain 1 to ${MAX_PIXEL_TEXT_LENGTH} characters.`,
        });
      } else if (geometry.text.trim().length === 0) {
        issues.push({
          severity: 'error',
          code: 'geometry.empty_text',
          path: `${path}.text`,
          message: 'pixelText.text must contain a visible character.',
        });
      } else if (!PIXEL_TEXT_PATTERN.test(geometry.text)) {
        issues.push({
          severity: 'error',
          code: 'geometry.invalid_text_characters',
          path: `${path}.text`,
          message:
            'pixelText.text only allows uppercase A-Z, digits, spaces and .:/- punctuation.',
        });
      }
      break;
    case 'vectorText':
      positive(geometry.width, 'width');
      positive(geometry.height, 'height');
      positive(geometry.depth, 'depth');
      if (geometry.fontId !== 'helvetiker-bold') {
        issues.push({
          severity: 'error',
          code: 'geometry.invalid_font',
          path: `${path}.fontId`,
          message: 'vectorText.fontId must name a bundled approved font.',
        });
      }
      if (
        geometry.text.length === 0 ||
        geometry.text.length > 32
      ) {
        issues.push({
          severity: 'error',
          code: 'geometry.invalid_text_length',
          path: `${path}.text`,
          message: 'vectorText.text must contain 1 to 32 characters.',
        });
      } else if (geometry.text.trim().length === 0) {
        issues.push({
          severity: 'error',
          code: 'geometry.empty_text',
          path: `${path}.text`,
          message: 'vectorText.text must contain a visible character.',
        });
      } else if (!PIXEL_TEXT_PATTERN.test(geometry.text)) {
        issues.push({
          severity: 'error',
          code: 'geometry.invalid_text_characters',
          path: `${path}.text`,
          message:
            'vectorText.text only allows uppercase A-Z, digits, spaces and .:/- punctuation.',
        });
      }
      break;
  }
};

const findCycles = (spec: SceneSpecV1): readonly string[] => {
  const parents = new Map(
    spec.nodes.map((node) => [node.id, node.parentNodeId] as const),
  );
  const cyclic = new Set<string>();

  for (const node of spec.nodes) {
    const visiting = new Set<string>();
    let cursor: string | undefined = node.id;
    while (cursor) {
      if (visiting.has(cursor)) {
        visiting.forEach((id) => cyclic.add(id));
        break;
      }
      visiting.add(cursor);
      cursor = parents.get(cursor);
    }
  }

  return [...cyclic].sort();
};

export const validateSceneSpec = (
  spec: SceneSpecV1,
  requestBudget?: GenerationBudget,
): ValidationReport => {
  const issues: ValidationIssue[] = [];
  const error = (code: string, path: string, message: string): void => {
    issues.push({ severity: 'error', code, path, message });
  };
  const warning = (code: string, path: string, message: string): void => {
    issues.push({ severity: 'warning', code, path, message });
  };

  if (spec.schemaVersion !== '1.0') {
    error(
      'schema.unsupported_version',
      'schemaVersion',
      'Only SceneSpec schemaVersion 1.0 is supported.',
    );
  }
  if (!ID_PATTERN.test(spec.id)) {
    error('id.invalid', 'id', 'Scene id must be a lowercase kebab-case slug.');
  }
  if (!isFiniteVector(spec.boundsHint, 3)) {
    error('bounds.invalid', 'boundsHint', 'boundsHint must be a finite Vec3.');
  } else if (spec.boundsHint.some((value) => value <= 0)) {
    error('bounds.non_positive', 'boundsHint', 'All bounds must be positive.');
  }

  const materialIds = new Set<string>();
  spec.materials.forEach((material, index) => {
    const path = `materials[${index}]`;
    if (!ID_PATTERN.test(material.id)) {
      error('id.invalid', `${path}.id`, 'Material id must be kebab-case.');
    }
    if (materialIds.has(material.id)) {
      error('id.duplicate', `${path}.id`, `Duplicate material id ${material.id}.`);
    }
    materialIds.add(material.id);
    if (!COLOR_PATTERN.test(material.baseColor)) {
      error(
        'material.invalid_color',
        `${path}.baseColor`,
        'baseColor must be a six-digit hexadecimal color.',
      );
    }
    if (material.emissive && !COLOR_PATTERN.test(material.emissive)) {
      error(
        'material.invalid_color',
        `${path}.emissive`,
        'emissive must be a six-digit hexadecimal color.',
      );
    }
    for (const [field, value] of [
      ['metalness', material.metalness],
      ['roughness', material.roughness],
      ['clearcoat', material.clearcoat],
      ['clearcoatRoughness', material.clearcoatRoughness],
      ['opacity', material.opacity],
      ['normalScale', material.normalScale],
      ['transmission', material.transmission],
    ] as const) {
      if (
        value !== undefined &&
        (!isFiniteNumber(value) || value < 0 || value > 1)
      ) {
        error(
          'material.range',
          `${path}.${field}`,
          `${field} must be within [0, 1].`,
        );
      }
    }
    if (
      material.envMapIntensity !== undefined &&
      (!isFiniteNumber(material.envMapIntensity) ||
        material.envMapIntensity < 0 ||
        material.envMapIntensity > 4)
    ) {
      error(
        'material.range',
        `${path}.envMapIntensity`,
        'envMapIntensity must be within [0, 4].',
      );
    }
    if (
      material.ior !== undefined &&
      (!isFiniteNumber(material.ior) ||
        material.ior < 1 ||
        material.ior > 2.5)
    ) {
      error(
        'material.range',
        `${path}.ior`,
        'ior must be within [1, 2.5].',
      );
    }
    if (
      material.thickness !== undefined &&
      (!isFiniteNumber(material.thickness) ||
        material.thickness < 0 ||
        material.thickness > 0.1)
    ) {
      error(
        'material.range',
        `${path}.thickness`,
        'thickness must be within [0, 0.1] meters.',
      );
    }
    if (
      material.surfacePattern !== undefined &&
      !SURFACE_PATTERNS.has(material.surfacePattern)
    ) {
      error(
        'material.invalid_surface_pattern',
        `${path}.surfacePattern`,
        'surfacePattern must name a deterministic built-in pattern.',
      );
    }
    if (
      material.emissiveIntensity !== undefined &&
      (!isFiniteNumber(material.emissiveIntensity) ||
        material.emissiveIntensity < 0)
    ) {
      error(
        'material.range',
        `${path}.emissiveIntensity`,
        'emissiveIntensity must be finite and non-negative.',
      );
    }
  });

  const partIds = new Set<string>();
  spec.parts.forEach((part, index) => {
    const path = `parts[${index}]`;
    if (!ID_PATTERN.test(part.id)) {
      error('id.invalid', `${path}.id`, 'Part id must be kebab-case.');
    }
    if (partIds.has(part.id)) {
      error('id.duplicate', `${path}.id`, `Duplicate part id ${part.id}.`);
    }
    partIds.add(part.id);
    if (!part.semanticClass.trim()) {
      error(
        'part.missing_semantic_class',
        `${path}.semanticClass`,
        'Every part needs a semantic class.',
      );
    }
    if (!isFiniteVector(part.explodeDirection, 3)) {
      error(
        'part.invalid_explode_direction',
        `${path}.explodeDirection`,
        'explodeDirection must be a finite Vec3.',
      );
    }
  });

  const nodeIds = new Set<string>();
  let estimatedTriangles = 0;
  let meshNodes = 0;
  spec.nodes.forEach((node, index) => {
    const path = `nodes[${index}]`;
    if (!ID_PATTERN.test(node.id)) {
      error('id.invalid', `${path}.id`, 'Node id must be kebab-case.');
    }
    if (nodeIds.has(node.id)) {
      error('id.duplicate', `${path}.id`, `Duplicate node id ${node.id}.`);
    }
    nodeIds.add(node.id);
    if (!isFiniteVector(node.transform.position, 3)) {
      error(
        'transform.invalid_position',
        `${path}.transform.position`,
        'Position must be a finite Vec3.',
      );
    }
    if (!isFiniteVector(node.transform.rotation, 4)) {
      error(
        'transform.invalid_rotation',
        `${path}.transform.rotation`,
        'Rotation must be a finite quaternion.',
      );
    }
    if (
      !isFiniteVector(node.transform.scale, 3) ||
      node.transform.scale.some((value) => value <= 0)
    ) {
      error(
        'transform.invalid_scale',
        `${path}.transform.scale`,
        'Scale must contain finite positive values.',
      );
    }
    if (node.kind === 'mesh') {
      meshNodes += 1;
      if (!partIds.has(node.partId)) {
        error(
          'reference.unknown_part',
          `${path}.partId`,
          `Unknown part ${node.partId}.`,
        );
      }
      if (!materialIds.has(node.materialId)) {
        error(
          'reference.unknown_material',
          `${path}.materialId`,
          `Unknown material ${node.materialId}.`,
        );
      }
      validateGeometry(node.geometry, `${path}.geometry`, issues);
      estimatedTriangles += estimateTriangles(node.geometry);
    }
  });

  spec.nodes.forEach((node, index) => {
    if (node.parentNodeId && !nodeIds.has(node.parentNodeId)) {
      error(
        'reference.unknown_parent',
        `nodes[${index}].parentNodeId`,
        `Unknown parent node ${node.parentNodeId}.`,
      );
    }
  });
  const cycles = findCycles(spec);
  if (cycles.length > 0) {
    error(
      'hierarchy.cycle',
      'nodes',
      `Node hierarchy contains a cycle: ${cycles.join(', ')}.`,
    );
  }

  const jointIds = new Set<string>();
  spec.joints.forEach((joint, index) => {
    const path = `joints[${index}]`;
    if (jointIds.has(joint.id)) {
      error('id.duplicate', `${path}.id`, `Duplicate joint id ${joint.id}.`);
    }
    jointIds.add(joint.id);
    if (!nodeIds.has(joint.nodeId)) {
      error(
        'reference.unknown_joint_node',
        `${path}.nodeId`,
        `Unknown joint node ${joint.nodeId}.`,
      );
    }
    if (!isFiniteVector(joint.axis, 3) || magnitude(joint.axis) < 1e-8) {
      error(
        'joint.invalid_axis',
        `${path}.axis`,
        'Joint axis must be a non-zero finite Vec3.',
      );
    }
    if (
      !isFiniteNumber(joint.min) ||
      !isFiniteNumber(joint.max) ||
      joint.min > joint.max
    ) {
      error(
        'joint.invalid_limits',
        path,
        'Joint limits must be finite and min <= max.',
      );
    }
    if (
      !isFiniteNumber(joint.defaultValue) ||
      joint.defaultValue < joint.min ||
      joint.defaultValue > joint.max
    ) {
      error(
        'joint.default_out_of_range',
        `${path}.defaultValue`,
        'Joint defaultValue must be inside its limits.',
      );
    }
  });

  const actionIds = new Set<string>();
  spec.actions.forEach((action, index) => {
    const path = `actions[${index}]`;
    if (actionIds.has(action.id)) {
      error('id.duplicate', `${path}.id`, `Duplicate action id ${action.id}.`);
    }
    actionIds.add(action.id);
    if (!jointIds.has(action.jointId)) {
      error(
        'reference.unknown_action_joint',
        `${path}.jointId`,
        `Unknown action joint ${action.jointId}.`,
      );
    }
    if (
      !isFiniteNumber(action.minValue) ||
      !isFiniteNumber(action.maxValue) ||
      action.minValue > action.maxValue ||
      !isFiniteNumber(action.defaultValue) ||
      action.defaultValue < action.minValue ||
      action.defaultValue > action.maxValue
    ) {
      error(
        'action.invalid_range',
        path,
        'Action values must be finite with default inside [min, max].',
      );
    }
    if (
      action.kind === 'oscillate' &&
      (!isFiniteNumber(action.frequencyHz) || action.frequencyHz <= 0)
    ) {
      error(
        'action.invalid_frequency',
        `${path}.frequencyHz`,
        'Oscillation frequency must be greater than zero.',
      );
    }
  });

  const runtimeBindings = spec.runtimeBindings ?? [];
  const runtimeBindingIds = new Set<string>();
  const runtimeBindingNodeIds = new Set<string>();
  runtimeBindings.forEach((binding, index) => {
    const path = `runtimeBindings[${index}]`;
    if (!ID_PATTERN.test(binding.id)) {
      error('id.invalid', `${path}.id`, 'Runtime binding id must be kebab-case.');
    }
    if (runtimeBindingIds.has(binding.id)) {
      error(
        'id.duplicate',
        `${path}.id`,
        `Duplicate runtime binding id ${binding.id}.`,
      );
    }
    runtimeBindingIds.add(binding.id);
    if (binding.kind !== 'local-clock-text') {
      error(
        'runtime_binding.unsupported_kind',
        `${path}.kind`,
        'Only local-clock-text runtime bindings are supported.',
      );
    }
    if (!LOCAL_CLOCK_TEXT_FORMATS.has(binding.format)) {
      error(
        'runtime_binding.unsupported_format',
        `${path}.format`,
        'Local clock format must be HH:MM, SS, DD or M.DD.',
      );
    }
    const node = spec.nodes.find((candidate) => candidate.id === binding.nodeId);
    if (!node) {
      error(
        'reference.unknown_runtime_binding_node',
        `${path}.nodeId`,
        `Unknown runtime binding node ${binding.nodeId}.`,
      );
    } else if (node.kind !== 'mesh' || node.geometry.kind !== 'pixelText') {
      error(
        'runtime_binding.invalid_target',
        `${path}.nodeId`,
        'Local clock bindings may only target pixelText mesh nodes.',
      );
    }
    if (runtimeBindingNodeIds.has(binding.nodeId)) {
      error(
        'runtime_binding.duplicate_target',
        `${path}.nodeId`,
        `Node ${binding.nodeId} already has a runtime binding.`,
      );
    }
    runtimeBindingNodeIds.add(binding.nodeId);
  });

  const usedPartIds = new Set(
    spec.nodes
      .filter((node) => node.kind === 'mesh')
      .map((node) => node.partId),
  );
  spec.parts.forEach((part, index) => {
    if (!usedPartIds.has(part.id)) {
      error(
        'part.unbuilt',
        `parts[${index}]`,
        `Part ${part.id} has no mesh node.`,
      );
    }
  });

  const maxNodes = Math.min(
    spec.budgets.maxNodes,
    requestBudget?.maxNodes ?? Number.POSITIVE_INFINITY,
  );
  const maxTriangles = Math.min(
    spec.budgets.maxTriangles,
    requestBudget?.maxTriangles ?? Number.POSITIVE_INFINITY,
  );
  if (spec.nodes.length > maxNodes) {
    error(
      'budget.nodes_exceeded',
      'nodes',
      `${spec.nodes.length} nodes exceed the budget of ${maxNodes}.`,
    );
  }
  if (estimatedTriangles > maxTriangles) {
    error(
      'budget.triangles_exceeded',
      'nodes',
      `${estimatedTriangles} estimated triangles exceed ${maxTriangles}.`,
    );
  }
  if (spec.materials.length > spec.budgets.maxMaterials) {
    error(
      'budget.materials_exceeded',
      'materials',
      `${spec.materials.length} materials exceed ${spec.budgets.maxMaterials}.`,
    );
  }
  if (meshNodes > 0 && spec.parts.length / meshNodes < 0.15) {
    warning(
      'semantics.low_part_density',
      'parts',
      'Many meshes map to few semantic parts; confirm editability is sufficient.',
    );
  }

  const metrics: ValidationMetrics = {
    materials: spec.materials.length,
    parts: spec.parts.length,
    nodes: spec.nodes.length,
    meshNodes,
    joints: spec.joints.length,
    actions: spec.actions.length,
    runtimeBindings: runtimeBindings.length,
    selectableParts: spec.parts.filter((part) => part.selectable).length,
    estimatedTriangles,
  };
  return {
    passed: !issues.some((issue) => issue.severity === 'error'),
    issues,
    metrics,
    structuralHash: structuralHash({
      materials: spec.materials,
      parts: spec.parts,
      nodes: spec.nodes,
      joints: spec.joints,
      actions: spec.actions,
      runtimeBindings,
    }),
  };
};
